import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { FadeUp } from "../components/Motion";
import { TaglineMarquee } from "../components/TaglineMarquee";
import { IMAGES, MANIFESTO } from "../data/content";

export default function About() {
  return (
    <>
      <PageHeader
        overline="Our Story"
        title={["The house of", "Arkaari."]}
        subtitle="Arkaari began with a single conviction — that clothing should be crafted with purpose, made for the person who wears it, and carry a story worth keeping."
      />

      {/* Editorial split */}
      <section className="max-w-[1500px] mx-auto px-5 sm:px-8 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <FadeUp className="lg:col-span-6 img-frame rounded-sm lg:sticky lg:top-28">
            <img src={IMAGES.founder} alt="Ankita Mangtani" className="w-full h-[80vh] object-cover rounded-sm" />
          </FadeUp>
          <div className="lg:col-span-6">
            <FadeUp>
              <p className="overline text-[#C6A15B] mb-6">The Founder</p>
              <h2 className="font-serif font-light text-4xl sm:text-5xl tracking-tight leading-tight">
                Ankita Mangtani
              </h2>
            </FadeUp>
            <FadeUp delay={0.1}>
              <div className="space-y-6 text-[#7a7568] text-lg leading-relaxed mt-8">
                <p>
                  Ankita founded Arkaari to bridge two worlds she loves — the soulful, slow craft of
                  Indian tailoring, and the clean, confident language of modern design. Trained by
                  master tailors and driven by an eye for detail, she built Arkaari as an atelier where
                  every garment is personal.
                </p>
                <p>
                  For Ankita, customization is not a service — it is the philosophy. Each piece begins
                  with a conversation, then a sketch, a pattern, and finally a garment cut to a single
                  person's measurements and story.
                </p>
                <p className="font-serif italic text-2xl text-[#121212]">
                  “I don't make clothes for a size. I make them for a person.”
                </p>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      <TaglineMarquee dark items={["Timeless", "Handcrafted", "Elegant", "Indian", "Modern"]} />

      {/* Manifesto chapters */}
      <section data-testid="manifesto" className="max-w-[1500px] mx-auto px-5 sm:px-8 py-24 sm:py-32">
        <FadeUp>
          <p className="overline text-[#C6A15B] mb-16">What we stand for</p>
        </FadeUp>
        <div className="space-y-px">
          {MANIFESTO.map((m, i) => (
            <FadeUp key={m.n} delay={i * 0.08}>
              <div className="grid grid-cols-1 md:grid-cols-12 gap-6 py-12 border-t border-[#D5D0C5] items-baseline">
                <p className="md:col-span-2 font-serif text-7xl font-light text-[#C6A15B]/60">{m.n}</p>
                <h3 className="md:col-span-4 font-serif text-3xl sm:text-4xl font-light">{m.title}</h3>
                <p className="md:col-span-6 text-[#7a7568] text-lg leading-relaxed">{m.body}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#121212] text-[#F5F2EA] py-24 grain relative">
        <div className="max-w-[1500px] mx-auto px-5 sm:px-8 text-center">
          <FadeUp>
            <h2 className="font-serif font-light text-4xl sm:text-6xl tracking-tight">
              Let's create <span className="italic text-[#D9BE86]">something yours.</span>
            </h2>
            <Link to="/custom-orders" className="btn-gold inline-flex mt-10 px-10 py-4 overline">
              <span className="inline-flex items-center gap-2">Begin a custom order <ArrowUpRight size={16} strokeWidth={1.5} /></span>
            </Link>
          </FadeUp>
        </div>
      </section>
    </>
  );
}
